import React, { useState } from "react";
import {
  Autocomplete,
  Box, Button, Card, Chip, Collapse, Divider, FormControlLabel, InputAdornment,
  MenuItem, Stack, Switch, TextField, Typography,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import type { FilterState, Options } from "../types";

type Props = {
  value: FilterState;
  onChange: (v: FilterState) => void;
  onClear: () => void;
  loading: boolean;
  options: Options;
};

const humanMode = (m: string) =>
  m === "Online" ? "Online"
  : m === "Offline" || m === "in_person" || m === "In-Person" ? "In-Person"
  : m === "Hybrid" ? "Hybrid" : m;

export default function FilterPanel({ value, onChange, onClear, loading, options }: Props) {
  const [open, setOpen] = useState(false);
  const set = <K extends keyof FilterState>(k: K, v: FilterState[K]) => onChange({ ...value, [k]: v });

  const chips: { key: string; label: string; onDelete: () => void }[] = [];
  if (value.week_start) chips.push({ key: "wk", label: `Week: ${value.week_start}`, onDelete: () => set("week_start", "") });
  if (value.date_from) chips.push({ key: "df", label: `From: ${value.date_from}`, onDelete: () => set("date_from", "") });
  if (value.date_to) chips.push({ key: "dt", label: `To: ${value.date_to}`, onDelete: () => set("date_to", "") });
  value.location_ids.forEach((id) => chips.push({
    key: `l${id}`, label: `Location: ${options.locations.find((l) => l.id === id)?.label || id}`,
    onDelete: () => set("location_ids", value.location_ids.filter((x) => x !== id)),
  }));
  value.student_ids.forEach((id) => chips.push({
    key: `st${id}`, label: `Student: ${options.students.find((s) => s.id === id)?.label || id}`,
    onDelete: () => set("student_ids", value.student_ids.filter((x) => x !== id)),
  }));
  value.grade_ids.forEach((id) => chips.push({
    key: `g${id}`, label: `Grade: ${options.grades.find((g) => g.id === id)?.label || id}`,
    onDelete: () => set("grade_ids", value.grade_ids.filter((x) => x !== id)),
  }));
  value.staff_ids.forEach((id) => chips.push({
    key: `s${id}`, label: `Staff: ${options.staff.find((s) => s.id === id)?.label || id}`,
    onDelete: () => set("staff_ids", value.staff_ids.filter((x) => x !== id)),
  }));
  value.subject_ids.forEach((id) => chips.push({
    key: `sub${id}`, label: `Subject: ${options.subjects.find((s) => s.id === id)?.label || id}`,
    onDelete: () => set("subject_ids", value.subject_ids.filter((x) => x !== id)),
  }));
  value.study_modes.forEach((m) => chips.push({
    key: `m${m}`, label: `Mode: ${humanMode(m)}`,
    onDelete: () => set("study_modes", value.study_modes.filter((x) => x !== m)),
  }));
  if (value.include_one_off) chips.push({ key: "o", label: "Include one-off classes", onDelete: () => set("include_one_off", false) });
  if (value.student_search) chips.push({ key: "sr", label: `Search: ${value.student_search}`, onDelete: () => set("student_search", "") });

  return (
    <Card variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
        <TextField
          size="small" label="Week starting" type="date" InputLabelProps={{ shrink: true }}
          value={value.week_start}
          onChange={(e) => set("week_start", e.target.value)}
          disabled={loading}
          sx={{ minWidth: 170 }}
        />
        <TextField
          select size="small" label="Location"
          SelectProps={{ multiple: true, renderValue: (v: any) =>
            (v as number[]).map((id) => options.locations.find((l) => l.id === id)?.label || id).join(", ") }}
          value={value.location_ids}
          onChange={(e) => set("location_ids", e.target.value as any as number[])}
          sx={{ minWidth: 200 }}
        >
          {options.locations.map((l) => <MenuItem key={l.id} value={l.id}>{l.label}</MenuItem>)}
        </TextField>
        <Autocomplete
          multiple
          size="small"
          disableCloseOnSelect
          options={options.students}
          getOptionLabel={(s) => `${s.label}${s.reference_id ? ` (${s.reference_id})` : ""}`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          value={options.students.filter((s) => value.student_ids.includes(s.id))}
          onChange={(_, sel) => set("student_ids", sel.map((s) => s.id))}
          sx={{ minWidth: 260 }}
          renderInput={(params) => <TextField {...params} label="Student" placeholder="Search…" />}
          renderTags={(sel, getTagProps) =>
            sel.map((s, i) => <Chip {...getTagProps({ index: i })} key={s.id} size="small" label={s.label} />)
          }
        />
        <TextField
          size="small" label="Search student"
          placeholder="Name or reference"
          value={value.student_search}
          onChange={(e) => set("student_search", e.target.value)}
          InputProps={{
            startAdornment: (<InputAdornment position="start"><SearchOutlinedIcon fontSize="small" /></InputAdornment>),
          }}
          sx={{ minWidth: 200 }}
          disabled={loading}
        />
        <Button variant="outlined" size="small" startIcon={<TuneOutlinedIcon />}
          onClick={() => setOpen((x) => !x)}>
          {open ? "Hide filters" : "More filters"}
        </Button>
        <Button size="small" onClick={onClear}>Clear</Button>
      </Stack>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Divider sx={{ my: 2 }} />
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} flexWrap="wrap" useFlexGap>
          <TextField select size="small" label="Study Mode"
            SelectProps={{ multiple: true, renderValue: (v: any) => (v as string[]).map(humanMode).join(", ") }}
            value={value.study_modes}
            onChange={(e) => set("study_modes", e.target.value as any as string[])}
            sx={{ minWidth: 180 }}>
            {options.study_modes.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
          </TextField>

          <TextField select size="small" label="Grade"
            SelectProps={{ multiple: true, renderValue: (v: any) =>
              (v as number[]).map((id) => options.grades.find((g) => g.id === id)?.label || id).join(", ") }}
            value={value.grade_ids}
            onChange={(e) => set("grade_ids", e.target.value as any as number[])}
            sx={{ minWidth: 160 }}>
            {options.grades.map((g) => <MenuItem key={g.id} value={g.id}>{g.label}</MenuItem>)}
          </TextField>

          <TextField select size="small" label="Staff"
            SelectProps={{ multiple: true, renderValue: (v: any) =>
              (v as number[]).map((id) => options.staff.find((s) => s.id === id)?.label || id).join(", ") }}
            value={value.staff_ids}
            onChange={(e) => set("staff_ids", e.target.value as any as number[])}
            sx={{ minWidth: 200 }}>
            {options.staff.map((s) => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
          </TextField>

          <TextField select size="small" label="Subject"
            SelectProps={{ multiple: true, renderValue: (v: any) =>
              (v as number[]).map((id) => options.subjects.find((s) => s.id === id)?.label || id).join(", ") }}
            value={value.subject_ids}
            onChange={(e) => set("subject_ids", e.target.value as any as number[])}
            sx={{ minWidth: 180 }}>
            {options.subjects.map((s) => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
          </TextField>

          <TextField select size="small" label="Assignment Status"
            SelectProps={{ multiple: true, renderValue: (v: any) => (v as string[]).join(", ") }}
            value={value.assignment_statuses}
            onChange={(e) => set("assignment_statuses", e.target.value as any as string[])}
            sx={{ minWidth: 180 }}>
            {options.assignment_statuses.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>

          <TextField size="small" label="From" type="date" InputLabelProps={{ shrink: true }}
            value={value.date_from} onChange={(e) => set("date_from", e.target.value)}
            sx={{ minWidth: 160 }} />
          <TextField size="small" label="To" type="date" InputLabelProps={{ shrink: true }}
            value={value.date_to} onChange={(e) => set("date_to", e.target.value)}
            sx={{ minWidth: 160 }} />

          <FormControlLabel control={<Switch size="small" checked={value.include_one_off}
              onChange={(e) => set("include_one_off", e.target.checked)} />}
            label="Include one-off / extra classes" />
        </Stack>
      </Collapse>

      {chips.length > 0 && (
        <Box mt={1.5}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>Active filters:</Typography>
          {chips.map((c) => (
            <Chip key={c.key} size="small" label={c.label} onDelete={c.onDelete}
              sx={{ mr: 0.5, mb: 0.5 }} />
          ))}
          <Button size="small" onClick={onClear} sx={{ ml: 1 }}>Clear all</Button>
        </Box>
      )}
    </Card>
  );
}
